import { useState, useEffect } from 'react';
import { useAuth } from '@/features/auth';
import { ConfiguracoesService } from '../services/configuracoes.service';
import { toast } from 'sonner';
import type {
  ConfiguracoesState,
  UserProfile,
  UpdateTenantSettingsDto
} from '../types';

const initialState: ConfiguracoesState = {
  tenantSettings: {
    id: 0,
    tenant_id: 0,
    porcentagem_comissao: 5,
    documentos_obrigatorios: ['rg', 'cpf', 'comprovante_residencia'],
    email_notificacoes: '',
    dias_vencimento_padrao: 30,
    logo_url: null,
    cor_primaria: '#3b82f6',
    cor_secundaria: '#f59e0b',
    usa_assinatura_digital: true,
    texto_contrato_padrao: null,
    dia_inicio_producao: 1,
    created_at: new Date(),
    updated_at: new Date(),
    nome: '',
    cnpj: '',
    endereco: '',
    telefone: '',
    backup_automatico: true,
    modo_manutencao: false,
    logs_detalhados: true,
    notificar_novas_propostas: true,
    notificar_vencimentos: true,
    notificar_comissoes: true,
    notificar_atividades: true,
    notificar_relatorios: true,
  },
  profile: {
    id: 0,
    email: '',
    first_name: '',
    last_name: '',
    role: '',
    status: '',
    created_at: new Date(),
    updated_at: new Date(),
  },
  notifications: {
    emailNotifications: {
      newProposals: true,
      paymentDue: true,
      pendingCommissions: false,
    },
    pushNotifications: {
      importantActivities: true,
      weeklyReports: true,
    },
  },
  security: {
    twoFactorEnabled: false,
    activeSessions: [],
  },
  company: {
    name: '',
    cnpj: '',
    address: '',
    phone: '',
  },
  system: {
    autoBackup: true,
    maintenanceMode: false,
    detailedLogs: true,
  },
};

export function useConfiguracoes() {
  const { state: authState, updateUser } = useAuth();
  const user = authState.user;
  const [state, setState] = useState<ConfiguracoesState>(initialState);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      loadAllData();
    }
  }, [user?.id]);

  const loadAllData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Carregar dados do usuário
      const userData = await ConfiguracoesService.getCurrentUser();

      // Carregar configurações de segurança (sessões ativas, etc.)
      const securityData = await loadSecurityData();

      // Carregar dados adicionais do tenant de forma explícita
      const tId = (userData)?.tenant_id || (userData)?.tenantId || (user)?.tenant_id;
      
      let tenantSettingsData = null;
      if (tId) {
        console.log('Carregando configurações para tenant:', tId);
        tenantSettingsData = await ConfiguracoesService.getTenantSettings(Number(tId));
      }

      setState(prev => {
        const newState = {
          ...prev,
          profile: {
            ...userData,
            tenant: userData.tenant || user?.tenant || null,
          },
          security: securityData,
          tenantSettings: {
            ...prev.tenantSettings,
            tenant_id: tId ? Number(tId) : prev.tenantSettings.tenant_id,
            ...(tenantSettingsData || {}),
          }
        };

        // Garantir que campos numéricos sejam números
        if (newState.tenantSettings.dia_inicio_producao !== undefined) {
          newState.tenantSettings.dia_inicio_producao = Number(newState.tenantSettings.dia_inicio_producao);
        }

        return newState;
      });
    } catch (err) {
      console.error('Error loading configuration data:', err);
      setError('Erro ao carregar dados de configuração');
      toast.error('Erro ao carregar dados de configuração');
    } finally {
      setLoading(false);
    }
  };

  const loadSecurityData = async () => {
    try {
      // Simular dados de sessões ativas (já que Supabase não fornece isso diretamente)
      return {
        twoFactorEnabled: false,
        activeSessions: [
          {
            id: 'current',
            device: navigator.platform || 'Unknown',
            browser: navigator.userAgent.includes('Chrome') ? 'Chrome' :
              navigator.userAgent.includes('Firefox') ? 'Firefox' :
                navigator.userAgent.includes('Safari') ? 'Safari' : 'Unknown',
            lastActivity: new Date(),
            isCurrent: true,
          }
        ],
      };
    } catch (err) {
      console.warn('Error loading security data:', err);
      return {
        twoFactorEnabled: false,
        activeSessions: [],
      };
    }
  };

  const updateProfile = async (data: Partial<UserProfile>) => {
    try {
      setLoading(true);
      const updatedUser = await ConfiguracoesService.updateCurrentUser(data);
      setState(prev => ({
        ...prev,
        profile: updatedUser,
      }));
      toast.success('Perfil atualizado com sucesso');
    } catch (err) {
      toast.error('Erro ao atualizar perfil');
    } finally {
      setLoading(false);
    }
  };

  const updateAvatar = async (file: File) => {
    try {
      setLoading(true);
      const { avatar } = await ConfiguracoesService.updateAvatar(file);

      // Atualizar estado local
      setState(prev => ({
        ...prev,
        profile: {
          ...prev.profile,
          avatar,
        },
      }));

      // Sincronizar com contexto de auth para atualizar sidebar
      if (user) {
        updateUser({
          ...user,
          avatar,
        });
      }

      toast.success('Foto atualizada com sucesso');
    } catch (err) {
      console.error('Erro ao atualizar avatar:', err);
      toast.error('Erro ao atualizar foto');
    } finally {
      setLoading(false);
    }
  };

  const updatePassword = async (data: {
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
  }) => {
    try {
      setLoading(true);
      await ConfiguracoesService.updatePassword(data);
      toast.success('Senha atualizada com sucesso');
    } catch (err) {
      toast.error('Erro ao atualizar senha');
    } finally {
      setLoading(false);
    }
  };

  const terminateSession = async (sessionId: string) => {
    try {
      setLoading(true);
      await ConfiguracoesService.terminateSession(sessionId);
      setState(prev => ({
        ...prev,
        security: {
          ...prev.security,
          activeSessions: prev.security.activeSessions.filter(s => s.id !== sessionId),
        },
      }));
      toast.success('Sessão encerrada com sucesso');
    } catch (err) {
      toast.error('Erro ao encerrar sessão');
    } finally {
      setLoading(false);
    }
  };

  const toggleTwoFactor = async (enabled: boolean) => {
    try {
      setLoading(true);
      await ConfiguracoesService.toggleTwoFactor(enabled);
      setState(prev => ({
        ...prev,
        security: {
          ...prev.security,
          twoFactorEnabled: enabled,
        },
      }));
      toast.success(`Autenticação em duas etapas ${enabled ? 'ativada' : 'desativada'}`);
    } catch (err) {
      toast.error('Erro ao alterar configuração de 2FA');
    } finally {
      setLoading(false);
    }
  };

  const updateTenantSettings = async (settings: Partial<UpdateTenantSettingsDto>) => {
    if (!user?.tenant_id) {
      toast.error('Usuário não possui tenant associado');
      return;
    }

    try {
      setLoading(true);
      const updatedSettings = await ConfiguracoesService.updateTenantSettings(
        user.tenant_id,
        settings
      );
      setState(prev => ({
        ...prev,
        tenantSettings: updatedSettings,
      }));
      toast.success('Configurações atualizadas com sucesso');
    } catch (err) {
      toast.error('Erro ao atualizar configurações');
      setError('Erro ao atualizar configurações');
    } finally {
      setLoading(false);
    }
  };

  return {
    state,
    loading,
    error,
    updateProfile,
    updateAvatar,
    updatePassword,
    terminateSession,
    toggleTwoFactor,
    updateTenantSettings,
  };
} 